import { useCallback, useEffect, useRef, useState } from 'react';
import { activityFeedAPI } from 'services/swagger-api';
import { NotificationDto } from 'api/activity-feed';

// Context enum values matching backend
const CONTEXTE_NOTIFICATION_GLOBALE = 1;

export interface UseNotificationsOptions {
  pageSize?: number;
  autoLoad?: boolean;
}

export interface UseNotificationsReturn {
  notifications: NotificationDto[];
  unreadCount: number;
  totalCount: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  loadMore: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  /**
   * Ouverture du panneau = tout lu côté back (PUT notifications/read-all, sans body)
   * + badge à 0. Les lignes gardent leur style non-lu le temps de la consultation
   * (on voit encore ce qui était nouveau).
   */
  acknowledgeAll: () => Promise<void>;
  /** Recharge la première page ; renvoie true si le chargement a réussi. */
  refresh: () => Promise<boolean>;
}

export const useNotifications = (
  options: UseNotificationsOptions = {}
): UseNotificationsReturn => {
  const { pageSize = 10, autoLoad = true } = options;

  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const pageNumberRef = useRef(1);
  const isMountedRef = useRef(true);

  // Fetch only the unread count (lightweight call)
  const fetchUnreadCount = useCallback(async () => {
    try {
      // Fetch first page with small size just to get unread count
      const response = await activityFeedAPI.searchNotificationsAsync({
        searchNotificationsCommand: {
          pageNumber: 1,
          pageSize: 50 // Fetch enough to get a reasonable unread count
        }
      });

      if (isMountedRef.current) {
        const unread = response.items.filter(n => !n.isRead).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, []);

  // Initial load — renvoie true si le chargement a réussi
  const loadInitial = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    pageNumberRef.current = 1;

    try {
      const response = await activityFeedAPI.searchNotificationsAsync({
        searchNotificationsCommand: {
          pageNumber: 1,
          pageSize
        }
      });

      if (isMountedRef.current) {
        setNotifications(response.items);
        setTotalCount(response.totalCount);
        // Calculate unread count from items
        const unread = response.items.filter(n => !n.isRead).length;
        setUnreadCount(unread);
        setHasMore(response.items.length < response.totalCount);
      }
      return true;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error('Erreur de chargement'));
      }
      return false;
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [pageSize]);

  // Load more notifications (infinite scroll)
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    const nextPage = pageNumberRef.current + 1;

    try {
      const response = await activityFeedAPI.searchNotificationsAsync({
        searchNotificationsCommand: {
          pageNumber: nextPage,
          pageSize
        }
      });

      if (isMountedRef.current) {
        setNotifications(prev => [...prev, ...response.items]);
        setTotalCount(response.totalCount);
        pageNumberRef.current = nextPage;

        const totalLoaded = notifications.length + response.items.length;
        setHasMore(totalLoaded < response.totalCount);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error('Erreur de chargement'));
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [isLoadingMore, hasMore, pageSize, notifications.length]);

  // Mark a notification as read
  const markAsRead = useCallback(async (id: string) => {
    try {
      await activityFeedAPI.markAsReadAsync({
        id,
        markAsReadCommand: {
          contexte: CONTEXTE_NOTIFICATION_GLOBALE
        }
      });

      if (isMountedRef.current) {
        setNotifications(prev =>
          prev.map(n => (n.id === id ? { ...n, isRead: true } : n))
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Erreur lors du marquage comme lu:', err);
    }
  }, []);

  // Mark all notifications as read (une seule route back, sans body)
  const markAllAsRead = useCallback(async () => {
    try {
      await activityFeedAPI.markAllNotificationsAsReadAsync();

      if (isMountedRef.current) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Erreur lors du marquage comme lu:', err);
    }
  }, []);

  // Tout lu côté back sans toucher au style des lignes affichées (badge seulement)
  const acknowledgeAll = useCallback(async () => {
    try {
      await activityFeedAPI.markAllNotificationsAsReadAsync();
      if (isMountedRef.current) {
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Erreur lors du marquage comme lu:', err);
    }
  }, []);

  // Refresh notifications
  const refresh = useCallback(async () => {
    return loadInitial();
  }, [loadInitial]);

  // Initial load on mount
  useEffect(() => {
    isMountedRef.current = true;

    if (autoLoad) {
      loadInitial();
    } else {
      // Even if autoLoad is false, fetch the unread count for the badge
      fetchUnreadCount();
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [loadInitial, autoLoad, fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    totalCount,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    markAsRead,
    markAllAsRead,
    acknowledgeAll,
    refresh
  };
};
